import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Heart, Clock, MapPin, ArrowLeft, CheckCircle, Play,
  RefreshCw, ChevronRight, Compass, Target, Sparkles, Send
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const emotionalStates = [
  { value: 'grounded', label: 'Grounded', emoji: '🌳' },
  { value: 'stressed', label: 'Stressed', emoji: '😤' },
  { value: 'uncertain', label: 'Uncertain', emoji: '🤔' },
  { value: 'hopeful', label: 'Hopeful', emoji: '✨' },
  { value: 'depleted', label: 'Depleted', emoji: '😔' }
];

const locationContexts = [
  { value: 'work', label: 'At Work', icon: '💼' },
  { value: 'home', label: 'At Home', icon: '🏠' },
  { value: 'commute', label: 'Commuting', icon: '🚗' },
  { value: 'other', label: 'Other', icon: '📍' }
];

export default function DailyPractice() {
  const { user } = useAuth();
  const [practice, setPractice] = useState(null);
  const [allPractices, setAllPractices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [context, setContext] = useState('work');
  const [emotionalState, setEmotionalState] = useState('grounded');
  const [reflection, setReflection] = useState('');
  const [saving, setSaving] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [showAllPractices, setShowAllPractices] = useState(false);

  const fetchPractice = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/faith/practice/today?context=${context}&emotional_state=${emotionalState}`);
      if (res.ok) {
        setPractice(await res.json());
      }
    } catch (err) {
      console.error('Error fetching practice:', err);
    } finally {
      setLoading(false);
    }
  }, [context, emotionalState]);

  const fetchAllPractices = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/faith/practice/all`);
      if (res.ok) {
        const data = await res.json();
        setAllPractices(data.practices);
      }
    } catch (err) {
      console.error('Error fetching practices:', err);
    }
  }, []);

  useEffect(() => {
    fetchPractice();
    fetchAllPractices();
  }, [fetchPractice, fetchAllPractices]);

  const handleRefresh = () => {
    setLoading(true);
    setCompleted(false);
    setReflection('');
    fetchPractice();
  };

  const handleCompletePractice = async () => {
    if (!user) {
      toast.error('Please log in to track practices');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/faith/practice/log`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          location_context: context,
          time_available_minutes: practice.duration_minutes,
          emotional_state: emotionalState,
          practice_id: practice.id,
          module_id: practice.module_link,
          duration_actual_minutes: practice.duration_minutes,
          reflection_response: reflection || null,
          completed: true
        })
      });

      if (res.ok) {
        const data = await res.json();
        setCompleted(true);
        toast.success(`Practice logged! Total: ${data.practice_count} practices`);
      }
    } catch (err) {
      toast.error('Failed to log practice');
    } finally {
      setSaving(false);
    }
  };

  const selectPractice = (p) => {
    setPractice(p);
    setShowAllPractices(false);
    setCompleted(false);
    setReflection('');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-purple-50/30" data-testid="daily-practice">
      {/* Header */}
      <div className="bg-gradient-to-br from-purple-600 to-indigo-700 text-white py-12">
        <div className="container mx-auto px-4">
          <Link to="/faith" className="inline-flex items-center text-white/80 hover:text-white mb-6">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Faith Hub
          </Link>
          
          <div className="flex items-start gap-4">
            <div className="p-4 bg-white/20 rounded-xl">
              <Heart className="h-10 w-10" />
            </div>
            <div>
              <Badge className="mb-2 bg-white/20 text-white border-0">Daily Spiritual Practice</Badge>
              <h1 className="text-3xl md:text-4xl font-bold mb-2">Ground Your Day</h1>
              <p className="text-purple-100 max-w-2xl">
                Short, grounded exercises you can do during your workday. 
                1-5 minutes to reset, reflect, and reconnect.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-3xl">
        {/* Context Selection */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-lg">Where are you right now?</CardTitle>
            <CardDescription>This helps us suggest the right practice for your moment.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Location */}
            <div>
              <Label className="text-sm text-stone-600 mb-3 block">Your Location</Label>
              <div className="flex flex-wrap gap-2">
                {locationContexts.map(loc => (
                  <button
                    key={loc.value}
                    onClick={() => { setContext(loc.value); handleRefresh(); }}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      context === loc.value 
                        ? 'bg-purple-600 text-white' 
                        : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                    }`}
                  >
                    {loc.icon} {loc.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Emotional State */}
            <div>
              <Label className="text-sm text-stone-600 mb-3 block">How are you feeling?</Label>
              <div className="flex flex-wrap gap-2">
                {emotionalStates.map(state => (
                  <button
                    key={state.value}
                    onClick={() => { setEmotionalState(state.value); handleRefresh(); }}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      emotionalState === state.value 
                        ? 'bg-purple-600 text-white' 
                        : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                    }`}
                  >
                    {state.emoji} {state.label}
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Today's Practice */}
        {practice && !showAllPractices && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className={`mb-8 border-2 ${completed ? 'border-green-300 bg-green-50/50' : 'border-purple-200 bg-purple-50/50'}`}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <Badge className={completed ? 'bg-green-100 text-green-700' : 'bg-purple-100 text-purple-700'}>
                      {completed ? <CheckCircle className="h-3 w-3 mr-1" /> : <Sparkles className="h-3 w-3 mr-1" />}
                      {completed ? 'Completed' : "Today's Practice"}
                    </Badge>
                    <CardTitle className="text-xl mt-2">{practice.name}</CardTitle>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-purple-700 border-purple-300">
                      <Clock className="h-3 w-3 mr-1" /> {practice.duration_minutes} min
                    </Badge>
                    <Button variant="ghost" size="icon" onClick={handleRefresh}>
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Instructions */}
                <div className="p-4 bg-white rounded-lg">
                  <p className="text-stone-700 leading-relaxed">{practice.instructions}</p>
                </div>

                {/* Reflection Question */}
                <div className="border-t pt-6">
                  <Label className="text-sm text-stone-600 mb-2 block">
                    <Target className="h-4 w-4 inline mr-1" />
                    Reflection: {practice.reflection}
                  </Label>
                  
                  {!completed && (
                    <>
                      <Textarea
                        value={reflection}
                        onChange={(e) => setReflection(e.target.value)}
                        placeholder="Take a moment to reflect... (optional)"
                        className="min-h-[100px] mt-2 mb-4"
                      />
                      
                      <Button 
                        onClick={handleCompletePractice}
                        disabled={saving || !user}
                        className="w-full bg-purple-600 hover:bg-purple-700"
                      >
                        {saving ? 'Saving...' : 'Complete Practice'}
                        <CheckCircle className="h-4 w-4 ml-2" />
                      </Button>
                      
                      {!user && (
                        <p className="text-sm text-stone-500 text-center mt-2">
                          <Link to="/login" className="text-purple-600 hover:underline">Log in</Link> to track your practices
                        </p>
                      )}
                    </>
                  )}
                  
                  {completed && (
                    <div className="text-center py-4">
                      <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-2" />
                      <p className="text-green-700 font-medium">Practice complete! Well done.</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* All Practices Toggle */}
        <div className="text-center mb-8">
          <Button 
            variant="outline" 
            onClick={() => setShowAllPractices(!showAllPractices)}
          >
            {showAllPractices ? 'Hide All Practices' : 'Browse All Practices'}
            <ChevronRight className={`h-4 w-4 ml-1 transition-transform ${showAllPractices ? 'rotate-90' : ''}`} />
          </Button>
        </div>

        {/* All Practices List */}
        {showAllPractices && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="space-y-4"
          >
            {allPractices.map((p) => (
              <Card 
                key={p.id} 
                className={`cursor-pointer hover:shadow-md transition-shadow ${
                  practice?.id === p.id ? 'border-purple-300 bg-purple-50' : ''
                }`}
                onClick={() => selectPractice(p)}
              >
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-stone-800">{p.name}</h3>
                      <p className="text-sm text-stone-500">
                        {p.context.join(', ')} • {p.emotional_fit.join(', ')}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{p.duration_minutes} min</Badge>
                      <Play className="h-4 w-4 text-purple-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </motion.div>
        )}

        {/* Connected Module */}
        {practice && !showAllPractices && (
          <Card className="bg-stone-100 border-stone-200">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-stone-600">
                  <Compass className="h-4 w-4" />
                  <span className="text-sm">From the curriculum: Module {practice.module_link}</span>
                </div>
                <Link to={`/faith/curriculum/${practice.module_link}`}>
                  <Button variant="ghost" size="sm">
                    Learn More <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
